import { createFileRoute, Navigate, Outlet, redirect } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated")({
  head: () => ({ meta: [{ name: "robots", content: "noindex, nofollow" }] }),
  // Skip SSR for the entire authenticated subtree. The Supabase session lives
  // in localStorage (client-only), so server-rendered HTML can never know the
  // user is signed in — rendering it produces a brief login-form flash on
  // refresh before the client hydrates and corrects state. Client-only render
  // eliminates that mismatch entirely.
  ssr: false,
  beforeLoad: async () => {
    let { data } = await supabase.auth.getSession();
    // If no session in storage yet, give Supabase one chance to hydrate
    // (handles slow networks and post-OAuth redirects) before bouncing.
    if (!data.session) {
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        ({ data } = await supabase.auth.getSession());
      }
    }
    if (!data.session) {
      throw redirect({ to: "/auth", search: { tab: "login" } });
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { user, loading, isHydrated } = useAuth();

  // Wait until Supabase has restored the session from localStorage before
  // deciding to redirect. Otherwise the first client paint sees user=null
  // and briefly renders the login page even though a valid session exists.
  if (!isHydrated || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" search={{ tab: "login" }} />;
  }

  return <Outlet />;
}
